"use client"

import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Loader2,
  Search,
  Shield,
  Filter,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  Settings
} from "lucide-react"
import toast from "react-hot-toast"

interface Control {
  id: string
  name: string
  description: string
  type: string
  effectiveness: string
  lastAssessed: string
  reference: string
  verifier: string
  adequacyName: string
  performanceName: string
  frequencyName: string
  typeName: string
  designName: string
  ratingName: string
  ratingScore: number
  linkedRisksCount: number
}

interface ControlSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedControls: Control[]) => void
  selectedControlIds?: Set<string>
  title?: string
  description?: string
}

export default function ControlSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  selectedControlIds = new Set(),
  title = "Manage Linked Controls",
  description = "Select controls from the governance model to link to this assignment."
}: ControlSelectionModalProps) {
  const [controls, setControls] = useState<Control[]>([])
  const [filteredControls, setFilteredControls] = useState<Control[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedControlIds))
  
  // Filter states
  const [effectivenessFilter, setEffectivenessFilter] = useState<string>("all")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [ratingFilter, setRatingFilter] = useState<string>("all")
  
  // Available filter options
  const [controlTypes, setControlTypes] = useState<string[]>([])
  const [effectivenessLevels] = useState<string[]>(["Effective", "Partially Effective", "Ineffective", "Not Tested"])
  const [ratings, setRatings] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      fetchControls()
    }
  }, [isOpen])

  useEffect(() => {
    setSelectedIds(new Set(selectedControlIds))
  }, [selectedControlIds])

  useEffect(() => {
    filterControls()
  }, [controls, searchTerm, effectivenessFilter, typeFilter, ratingFilter])

  const fetchControls = async () => {
    try {
      setLoading(true)
      
      const response = await fetch('/api/controls')
      if (!response.ok) {
        throw new Error(`Failed to fetch controls: ${response.statusText}`)
      }
      
      const data = await response.json()
      setControls(data)
      
      // Extract unique values for filters
      const uniqueTypes = [...new Set(data.map((control: Control) => control.typeName).filter(Boolean))] as string[]
      const uniqueRatings = [...new Set(data.map((control: Control) => control.ratingName).filter(Boolean))] as string[]

      setControlTypes(uniqueTypes)
      setRatings(uniqueRatings)
      
    } catch (error) {
      console.error("Failed to fetch controls:", error)
      toast.error("Failed to load controls")
    } finally {
      setLoading(false)
    }
  }

  const filterControls = () => {
    let filtered = controls

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(control =>
        control.name.toLowerCase().includes(search) ||
        control.description?.toLowerCase().includes(search) ||
        control.typeName?.toLowerCase().includes(search) ||
        control.reference?.toLowerCase().includes(search)
      )
    }

    // Effectiveness filter
    if (effectivenessFilter !== "all") {
      filtered = filtered.filter(control => control.effectiveness === effectivenessFilter)
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(control => control.typeName === typeFilter)
    }

    // Rating filter
    if (ratingFilter !== "all") {
      filtered = filtered.filter(control => control.ratingName === ratingFilter)
    }

    setFilteredControls(filtered)
  }

  const handleControlToggle = (controlId: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(controlId)) {
      newSelected.delete(controlId)
    } else {
      newSelected.add(controlId)
    }
    setSelectedIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedIds.size === filteredControls.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredControls.map(control => control.id)))
    }
  }

  const handleConfirm = () => {
    const selectedControls = controls.filter(control => selectedIds.has(control.id))
    onConfirm(selectedControls)
    onClose()
  }

  const handleCancel = () => {
    setSelectedIds(new Set(selectedControlIds))
    onClose()
  }

  const clearFilters = () => {
    setSearchTerm("")
    setEffectivenessFilter("all")
    setTypeFilter("all")
    setRatingFilter("all")
  }

  const getEffectivenessBadgeVariant = (effectiveness: string) => {
    switch (effectiveness?.toLowerCase()) {
      case "effective":
        return "bg-green-100 text-green-700 border-green-300"
      case "partially effective":
        return "bg-yellow-100 text-yellow-700 border-yellow-300"
      case "ineffective":
        return "bg-red-100 text-red-700 border-red-300"
      case "not tested":
        return "bg-gray-100 text-gray-700 border-gray-300"
      default:
        return "bg-blue-100 text-blue-700 border-blue-300"
    }
  }

  const getRatingIcon = (ratingScore: number) => {
    if (ratingScore >= 4) return <CheckCircle className="h-4 w-4 text-green-500" />
    if (ratingScore >= 3) return <AlertCircle className="h-4 w-4 text-yellow-500" />
    return <X className="h-4 w-4 text-red-500" />
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "Never"
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return "Invalid date"
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading controls...
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search controls by name, description, type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Select value={effectivenessFilter} onValueChange={setEffectivenessFilter}>
                  <SelectTrigger className="w-44">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Effectiveness" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Effectiveness</SelectItem>
                    {effectivenessLevels.map(level => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40">
                    <Settings className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {controlTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={ratingFilter} onValueChange={setRatingFilter}>
                  <SelectTrigger className="w-40">
                    <Star className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ratings</SelectItem>
                    {ratings.map(rating => (
                      <SelectItem key={rating} value={rating}>{rating}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>

            <Separator />

            {/* Selection Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedIds.size === filteredControls.length && filteredControls.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm font-medium">
                  Select All ({selectedIds.size} of {filteredControls.length} selected)
                </span>
              </div>
              <Badge variant="outline">
                {filteredControls.length} controls found
              </Badge>
            </div>

            {/* Control List */}
            <ScrollArea className="h-96">
              <div className="space-y-2">
                {filteredControls.length > 0 ? (
                  filteredControls.map((control) => (
                    <div
                      key={control.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedIds.has(control.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                      onClick={() => handleControlToggle(control.id)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedIds.has(control.id)}
                          onCheckedChange={() => handleControlToggle(control.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium">{control.name}</h4>
                              {control.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {control.description}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              {control.ratingScore && (
                                <div className="flex items-center gap-1">
                                  {getRatingIcon(control.ratingScore)}
                                  <Badge variant="outline" className="text-xs">
                                    {control.ratingName}
                                  </Badge>
                                </div>
                              )}
                              <Badge className={getEffectivenessBadgeVariant(control.effectiveness)}>
                                {control.effectiveness}
                              </Badge>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            {control.typeName && (
                              <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Type:</span>
                                <span>{control.typeName}</span>
                              </div>
                            )}
                            
                            {control.frequencyName && (
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Frequency:</span>
                                <span>{control.frequencyName}</span>
                              </div>
                            )}
                            
                            {control.designName && (
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Design:</span>
                                <span>{control.designName}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-4">
                              {control.adequacyName && (
                                <div>
                                  <span className="text-muted-foreground">Adequacy:</span>
                                  <Badge variant="outline" className="ml-1 text-xs">
                                    {control.adequacyName}
                                  </Badge>
                                </div>
                              )}
                              {control.performanceName && (
                                <div>
                                  <span className="text-muted-foreground">Performance:</span>
                                  <Badge variant="outline" className="ml-1 text-xs">
                                    {control.performanceName}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 text-muted-foreground">
                              {control.linkedRisksCount > 0 && (
                                <span>{control.linkedRisksCount} linked risk{control.linkedRisksCount !== 1 ? 's' : ''}</span>
                              )}
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span className="text-xs">
                                  Last assessed: {formatDate(control.lastAssessed)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No controls found matching your criteria</p>
                    <p className="text-sm mt-2">Try adjusting your search or filters</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            Confirm Selection ({selectedIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
